import { useQuery } from '@tanstack/react-query';
import { StatsSection } from '../components/dashboard/StatsSection.js';
import { ActivitySection } from '../components/dashboard/ActivitySection.js';
import { QuickActions } from '../components/dashboard/QuickActions.js';
import { ContinueReading } from '../components/dashboard/ContinueReading.js';
import { CoursesSection } from '../components/dashboard/CoursesSection.js';
import { LibrarySection } from '../components/dashboard/LibrarySection.js';
import {
  useStreak,
  useProfile,
  useActivityStats,
} from '../features/gamification/hooks/useGamification.js';
import { useProgressList } from '../features/progress/hooks/useProgress.js';
import { useCourseList } from '../features/courses/hooks/useCourses.js';
import { fetchLibraryStats } from '../services/library.js';
import { getXpForNextLevel, getLevelProgress, getLevelTitle } from '@xdface-vault/shared';

export function Dashboard() {
  const { data: streak } = useStreak();
  const { data: profile } = useProfile();
  const { data: activityData } = useActivityStats();
  const {
    data: progressItems,
    isLoading: progressLoading,
    isError: progressError,
    refetch: refetchProgress,
  } = useProgressList();
  const { data: courses = [], isLoading: coursesLoading } = useCourseList();
  const {
    data: libraryStats,
    isLoading: libraryLoading,
    isError: libraryError,
  } = useQuery({
    queryKey: ['library-stats'],
    queryFn: fetchLibraryStats,
  });

  const inProgressItems = (progressItems ?? [])
    .filter((item) => item.status === 'in-progress')
    .sort((a, b) => (Date.parse(b.updatedAt) || 0) - (Date.parse(a.updatedAt) || 0))
    .slice(0, 3);

  const xp = profile?.xp ?? 0;
  // El nivel viene calculado del servidor (getLevel(xp) en el api)
  const level = profile?.level ?? 1;
  const levelTitle = getLevelTitle(level);
  const xpProgress = getLevelProgress(xp);
  const xpForNext = getXpForNextLevel(level);

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div>
        <p className="font-hand text-[24px] leading-none text-[var(--xd-accent)]">tu rincón</p>
        <h1 className="mt-1 text-2xl font-bold text-[var(--color-text)]">Dashboard</h1>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">
          Tu centro de aprendizaje personal
        </p>
      </div>

      <ContinueReading
        items={inProgressItems}
        isLoading={progressLoading}
        isError={progressError && !progressItems}
        onRetry={() => void refetchProgress()}
      />

      <QuickActions />

      <CoursesSection courses={courses} isLoading={coursesLoading} />

      <LibrarySection stats={libraryStats} isLoading={libraryLoading} isError={libraryError} />

      <StatsSection
        streak={streak}
        xp={xp}
        level={level}
        levelTitle={levelTitle}
        xpProgress={xpProgress}
        xpForNext={xpForNext}
      />

      <ActivitySection data={activityData?.map((d) => ({ date: d.date, count: d.total })) ?? []} />
    </div>
  );
}
